import { useState } from 'react';
import type { ImageContent, ScriptElement } from '../../schema';

/**
 * Image renderer. `src` is already rewritten to a host-resolvable URL (the
 * AssetPort handles `lessonasset://` vs hosted URLs upstream), so this just
 * renders an <img>. On error we show a chalk-styled missing-image placeholder
 * so a single missing asset doesn't break the slide.
 */
export function ImageElement({
  element,
  fill,
}: {
  element: ScriptElement;
  fill?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const content = element.content as ImageContent | undefined;
  // rounded + shadow on by default, border off.
  const rounded = content?.rounded !== false;
  const showShadow = content?.shadow !== false;
  const bordered = !!content?.bordered;
  const radius = rounded ? (element.style?.borderRadius as number | undefined) ?? 10 : 0;
  const w = element.style?.width;
  const h = element.style?.height;

  if (!content?.src || failed) {
    return (
      <Placeholder
        label={content?.visualRef ?? content?.alt ?? 'image unavailable'}
      />
    );
  }

  // Decoration that hugs the *visible* image: `drop-shadow` follows the rounded
  // silhouette (a `box-shadow` traces the rectangular bounding box, which with
  // letterboxing is bigger than the picture), radius/border sit on the image.
  const deco: React.CSSProperties = {
    borderRadius: radius,
    filter: showShadow ? 'drop-shadow(0 8px 18px rgba(60, 45, 25, 0.28))' : undefined,
    border: bordered ? '1px solid rgba(60, 45, 25, 0.18)' : undefined,
  };

  // Optional crop (BLUEPRINT §13): the box is a viewport; the full image is
  // scaled so the [x,x+w]×[y,y+h] fraction fills it. The wrapper IS the visible
  // region, so the decoration goes on it.
  if (content.crop) {
    const { x, y, w: cw, h: ch } = content.crop;
    const sw = cw || 1;
    const sh = ch || 1;
    const box: React.CSSProperties = fill
      ? { width: '100%', height: '100%' }
      : {
          width: typeof w === 'number' ? `${w}px` : (w as string | undefined),
          height: typeof h === 'number' ? `${h}px` : (h as string | undefined),
          maxWidth: '100%',
        };
    return (
      <div style={{ ...box, ...deco, position: 'relative', overflow: 'hidden' }}>
        <img
          src={content.src}
          alt={content.alt ?? ''}
          onError={() => setFailed(true)}
          draggable={false}
          style={{
            position: 'absolute',
            width: `${100 / sw}%`,
            height: `${100 / sh}%`,
            left: `${-(x / sw) * 100}%`,
            top: `${-(y / sh) * 100}%`,
            maxWidth: 'none',
            objectFit: 'cover',
          }}
        />
      </div>
    );
  }

  // The <img> element is sized to the picture (not the bounding box), so the
  // radius/border/shadow hug the image with no letterbox margin. In fill mode a
  // centering wrapper holds the leftover box space.
  const img = (
    <img
      src={content.src}
      alt={content.alt ?? ''}
      onError={() => setFailed(true)}
      draggable={false}
      className="block"
      style={
        fill
          ? {
              // Scale WITH the box (both up and down) instead of being pinned at
              // the picture's natural size. `contain` keeps the aspect ratio and
              // never crops; `cover` fills the box and crops the overflow.
              width: '100%',
              height: '100%',
              objectFit: ((element.style?.objectFit as string | undefined) ??
                'contain') as React.CSSProperties['objectFit'],
              ...deco,
            }
          : {
              width: typeof w === 'number' ? `${w}px` : (w as string | undefined),
              height: typeof h === 'number' ? `${h}px` : (h as string | undefined),
              maxWidth: '100%',
              objectFit: 'contain',
              ...deco,
            }
      }
    />
  );

  if (fill) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {img}
      </div>
    );
  }
  return img;
}

function Placeholder({ label }: { label: string }) {
  return (
    <div
      className="flex h-full min-h-[140px] w-full flex-col items-center justify-center gap-2 rounded-xl"
      style={{
        border: '2px dashed rgba(60, 45, 25, 0.25)',
        background: 'rgba(60, 45, 25, 0.04)',
        color: 'rgba(60, 45, 25, 0.5)',
      }}
    >
      <span className="text-3xl opacity-60">🖼️</span>
      <span className="px-3 text-center text-[12px] italic tracking-wide">
        {label}
      </span>
    </div>
  );
}
